/**
 * Typed REST client for the Personal OS API.
 *
 * - openapi-fetch over the generated `paths` type (src/api/schema.d.ts)
 * - bearer middleware reads the token from localStorage on every request
 * - every non-2xx response is turned into an `ApiError` ({ error: { code, message, details? } })
 * - a 401 on any endpoint except login notifies the registered handler (router → /login)
 */
import createClient, { type Middleware } from 'openapi-fetch';
import type { paths } from './schema';
import { tokenStorage } from './token-storage';

/** API origin; '' = same origin (the Vite dev proxy forwards /api and /ws to the server). */
export const API_BASE: string = (import.meta.env.VITE_API_BASE ?? '').replace(/\/+$/, '');

const LOGIN_PATH = '/api/v1/auth/token';

export interface ApiErrorBody {
  error: { code: string; message: string; details?: unknown };
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }
  get isNotFound(): boolean {
    return this.status === 404;
  }
  get isConflict(): boolean {
    return this.status === 409;
  }
  get isNetwork(): boolean {
    return this.status === 0;
  }
}

export function isApiErrorBody(value: unknown): value is ApiErrorBody {
  if (!value || typeof value !== 'object') return false;
  const err = (value as { error?: unknown }).error;
  return !!err && typeof err === 'object' && typeof (err as { code?: unknown }).code === 'string' && typeof (err as { message?: unknown }).message === 'string';
}

function codeForStatus(status: number): string {
  switch (status) {
    case 400:
      return 'bad_request';
    case 401:
      return 'unauthorized';
    case 403:
      return 'forbidden';
    case 404:
      return 'not_found';
    case 409:
      return 'conflict';
    case 422:
      return 'validation_error';
    default:
      return status >= 500 ? 'server_error' : 'http_error';
  }
}

/** Build an ApiError from a fetch Response and whatever openapi-fetch parsed as its body. */
export function apiErrorFrom(response: Response, body: unknown): ApiError {
  const status = response.status;
  if (isApiErrorBody(body)) return new ApiError(status, body.error.code, body.error.message, body.error.details);
  if (typeof body === 'string' && body.trim().length > 0) return new ApiError(status, codeForStatus(status), body.trim().slice(0, 300));
  return new ApiError(status, codeForStatus(status), response.statusText ? `${response.statusText} (HTTP ${status})` : `HTTP ${status}`);
}

/** Normalise anything thrown around an API call (network failures, aborts) into an ApiError. */
export function toApiError(err: unknown): ApiError {
  if (err instanceof ApiError) return err;
  if (err instanceof Error) {
    if (err.name === 'AbortError') return new ApiError(0, 'aborted', 'Request aborted');
    return new ApiError(0, 'network_error', err.message || 'Network error: could not reach the server');
  }
  return new ApiError(0, 'unknown_error', String(err));
}

/** A minimal view of what every openapi-fetch call resolves to. */
export interface ApiResult<T> {
  data?: T;
  error?: unknown;
  response: Response;
}

/** Return `data` or throw an ApiError. Use for endpoints that return a body. */
export function unwrap<T>(result: ApiResult<T>): T {
  if (!result.response.ok || result.error !== undefined) throw apiErrorFrom(result.response, result.error);
  return result.data as T;
}

/** Throw an ApiError unless the response is 2xx. Use for 204 endpoints. */
export function ensureOk(result: ApiResult<unknown>): void {
  if (!result.response.ok || result.error !== undefined) throw apiErrorFrom(result.response, result.error);
}

export interface UnauthorizedInfo {
  request: Request;
  response: Response;
}

export interface ApiClientOptions {
  /** Defaults to API_BASE. Tests pass an absolute URL so Request construction works in Node. */
  baseUrl?: string;
  getToken?: () => string | null;
  onUnauthorized?: (info: UnauthorizedInfo) => void;
  fetch?: typeof globalThis.fetch;
}

export function createApiClient(options: ApiClientOptions = {}) {
  const getToken = options.getToken ?? (() => tokenStorage.get());
  const client = createClient<paths>({
    baseUrl: options.baseUrl ?? API_BASE,
    ...(options.fetch ? { fetch: options.fetch } : {}),
  });
  const bearer: Middleware = {
    onRequest({ request }) {
      const token = getToken();
      if (token && !request.headers.has('authorization')) request.headers.set('Authorization', `Bearer ${token}`);
      return request;
    },
    onResponse({ request, response, schemaPath }) {
      if (response.status === 401 && schemaPath !== LOGIN_PATH) options.onUnauthorized?.({ request, response });
      return response;
    },
  };
  client.use(bearer);
  return client;
}

export type ApiClient = ReturnType<typeof createApiClient>;

// ----------------------------------------------------------------- singleton

type UnauthorizedHandler = (info: UnauthorizedInfo) => void;
const unauthorizedHandlers = new Set<UnauthorizedHandler>();

/** Register a handler for 401 responses (the app routes to /login). Returns an unsubscribe function. */
export function onUnauthorized(handler: UnauthorizedHandler): () => void {
  unauthorizedHandlers.add(handler);
  return () => unauthorizedHandlers.delete(handler);
}

/** The application-wide client: token from localStorage, 401 → registered handlers. */
export const api = createApiClient({
  onUnauthorized: (info) => {
    for (const handler of unauthorizedHandlers) handler(info);
  },
});
